import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail } from "lucide-react";
import { api, errMsg, fmtSGT } from "@/lib/api";
import { AdminAccount } from "@/pages/admin/AdminAccount";
import { CONTACT_FIELDS } from "@/components/ContactLinks";

function Reminders() {
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/reminders").then(({ data }) => setInfo(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const sendNow = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/reminders/send-now"); toast.success(`Reminder run: ${data.sent} email(s) logged`); load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!info) return null;
  return (
    <div className="panel space-y-3 p-5" data-testid="admin-reminders">
      <div className="flex items-center justify-between">
        <p className="eyebrow">Email log · daily reminders & password resets</p>
        <span className="rounded-md bg-amber-500/20 px-2 py-0.5 font-mono text-[10px] uppercase text-amber-300" data-testid="admin-reminder-provider">{info.provider === "mock" ? "mocked · log only" : info.provider}</span>
      </div>
      <p className="text-sm text-slate-300">Sent daily at <span className="font-mono text-white">{String(info.reminder_hour_sgt).padStart(2, "0")}:00 SGT</span> to players who haven't submitted that day's set. {info.runs.length} run(s) so far.</p>
      <button onClick={sendNow} disabled={busy} className="btn-secondary w-full" data-testid="admin-reminder-send-now-button"><Mail size={16} /> {busy ? "Sending…" : "Send today's reminder now"}</button>
      <ul className="divide-y divide-slate-800 text-xs" data-testid="admin-reminder-log">
        {info.emails.length === 0 && <li className="py-2 text-slate-500">No emails logged yet.</li>}
        {info.emails.slice(0, 15).map((m) => (
          <li key={m.id} className="py-2 text-slate-400" data-testid={`admin-email-${m.id}`}>
            <button type="button" onClick={() => setOpen(open === m.id ? null : m.id)} className="w-full text-left hover:text-slate-200">
              <span className="text-slate-200">{m.to}</span> · {m.subject} · {fmtSGT(m.created_at)}
            </button>
            {open === m.id && <pre className="mt-2 whitespace-pre-wrap break-all rounded-lg bg-slate-900 p-3 font-sans text-[11px] text-slate-300" data-testid="admin-email-body">{m.body}</pre>}
          </li>
        ))}
      </ul>
      <p className="text-[11px] text-slate-500">Email delivery is mocked: reminders and password-reset links appear here only. Tap a row to read the message and copy a reset link for a player if needed.</p>
    </div>
  );
}

function toSgtInput(iso) {
  const d = new Date(new Date(iso).getTime() + 8 * 3600 * 1000);
  return d.toISOString().slice(0, 16);
}

export function ordinal(n) {
  const s = ["th", "st", "nd", "rd"], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

export function AdminSeason() {
  const [season, setSeason] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => api.get("/admin/season").then(({ data }) => {
    setSeason(data);
    const prizes = Array.from({ length: Math.max(5, ...(data.prizes || []).map((p) => p.place)) }, (_, i) => ({ place: i + 1, reward: (data.prizes || []).find((p) => p.place === i + 1)?.reward || "" }));
    const schedule = Object.fromEntries((data.day_schedule || []).map((o) => [o.day, toSgtInput(o.opens_at)]));
    setForm({ name: data.name, start_at: toSgtInput(data.start_at), total_days: data.total_days, day_length_hours: data.day_length_hours, prizes, schedule, contact: data.contact || {} });
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const prizes = form.prizes.filter((p) => p.reward.trim()).map((p) => ({ place: +p.place, reward: p.reward.trim() }));
      const day_schedule = Object.entries(form.schedule).filter(([d]) => +d <= +form.total_days).map(([d, v]) => ({ day: +d, opens_at: `${v}:00+08:00` }));
      await api.put("/admin/season", { name: form.name, start_at: `${form.start_at}:00+08:00`, total_days: +form.total_days, day_length_hours: +form.day_length_hours, prizes, day_schedule, contact: form.contact });
      toast.success("Season updated"); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setPrize = (i, reward) => setForm({ ...form, prizes: form.prizes.map((p, j) => (j === i ? { ...p, reward } : p)) });
  const addPlace = () => setForm({ ...form, prizes: [...form.prizes, { place: form.prizes.length + 1, reward: "" }] });
  const setDayOpen = (day, value) => setForm({ ...form, schedule: { ...form.schedule, [day]: value } });
  const resetDay = (day) => { const s = { ...form.schedule }; delete s[day]; setForm({ ...form, schedule: s }); };
  const effectiveOpen = (day) => {
    let t = new Date(`${form.start_at}:00+08:00`).getTime();
    for (let d = 2; d <= day; d++) t = form.schedule[d] != null ? new Date(`${form.schedule[d]}:00+08:00`).getTime() : t + form.day_length_hours * 3600 * 1000;
    return toSgtInput(new Date(t).toISOString());
  };

  if (!form) return <p className="eyebrow">Loading…</p>;
  return (
    <div className="space-y-4 md:grid md:grid-cols-2 md:items-start md:gap-6 md:space-y-0">
    <form onSubmit={save} className="panel space-y-4 p-5" data-testid="admin-season-form">
      <p className="eyebrow" data-testid="admin-season-status">Status: {season.status} · Day {season.day} of {season.total_days} · Ends {fmtSGT(season.end_at)} SGT</p>
      <label className="block"><span className="eyebrow mb-1 block">Name</span><input className="field" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-season-name-input" /></label>
      <label className="block"><span className="eyebrow mb-1 block">Day 1 opens (Singapore time)</span><input className="field" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="admin-season-start-input" /></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block"><span className="eyebrow mb-1 block">Competition length (days)</span><input className="field" type="number" min={1} max={60} value={form.total_days} onChange={(e) => setForm({ ...form, total_days: e.target.value })} data-testid="admin-season-days-input" /></label>
        <label className="block"><span className="eyebrow mb-1 block">Default gap between days (hours)</span><input className="field" type="number" min={1} value={form.day_length_hours} onChange={(e) => setForm({ ...form, day_length_hours: e.target.value })} data-testid="admin-season-hours-input" /></label>
      </div>
      <p className="text-xs text-slate-500">{form.total_days} days × 3 challenges = {form.total_days * 3} questions to publish. Use the Questions tab grid to fill every slot.</p>
      <div>
        <span className="eyebrow mb-2 block">Question release schedule (Singapore time)</span>
        <p className="mb-2 text-xs text-slate-500">By default each day opens {form.day_length_hours}h after the previous one. Override any day below — later days shift with it automatically unless they have their own time. A day stays open until the next day is released.</p>
        <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="admin-release-schedule">
          {Array.from({ length: Math.min(60, Math.max(1, +form.total_days || 1)) }, (_, i) => i + 1).map((d) => {
            const custom = form.schedule[d] != null;
            const live = season.days?.find((w) => w.day === d) && season.status === "live" && season.day === d;
            return (
              <div key={d} className="flex items-center gap-2" data-testid={`admin-release-day-${d}`}>
                <span className={`grid h-11 w-14 shrink-0 place-items-center rounded-lg font-mono text-xs font-bold ${live ? "bg-emerald-500/20 text-emerald-300" : "bg-slate-800 text-slate-300"}`}>D{d}</span>
                <input className="field py-2" type="datetime-local" value={custom ? form.schedule[d] : effectiveOpen(d)} onChange={(e) => setDayOpen(d, e.target.value)} data-testid={`admin-release-day-${d}-input`} />
                {custom
                  ? <button type="button" onClick={() => resetDay(d)} className="shrink-0 rounded-lg border border-slate-700 px-2 py-2 text-[11px] text-slate-300 hover:bg-slate-800" data-testid={`admin-release-day-${d}-reset`}>Reset</button>
                  : <span className="w-[52px] shrink-0 text-center font-mono text-[10px] uppercase text-slate-500">auto</span>}
              </div>
            );
          })}
        </div>
      </div>
      <div>
        <span className="eyebrow mb-2 block">Rewards at the end of the competition</span>
        <div className="space-y-2" data-testid="admin-prizes-list">
          {form.prizes.map((p, i) => (
            <div key={p.place} className="flex items-center gap-2">
              <span className="grid h-11 w-12 shrink-0 place-items-center rounded-lg bg-slate-800 font-mono text-sm font-bold text-amber-300">{ordinal(p.place)}</span>
              <input className="field" placeholder={`Reward for ${ordinal(p.place)} place (blank = none)`} value={p.reward} onChange={(e) => setPrize(i, e.target.value)} data-testid={`admin-prize-${p.place}-input`} />
            </div>
          ))}
        </div>
        <button type="button" onClick={addPlace} className="mt-2 text-sm font-medium text-blue-400 hover:text-blue-300" data-testid="admin-prize-add-button">+ Add another place</button>
      </div>
      <div>
        <span className="eyebrow mb-2 block">Contact & social media (shown on the landing page)</span>
        <div className="grid gap-2 sm:grid-cols-2" data-testid="admin-contact-fields">
          {CONTACT_FIELDS.map(({ key, label }) => (
            <input key={key} className="field py-2 text-sm" placeholder={label} value={form.contact[key] || ""} onChange={(e) => setForm({ ...form, contact: { ...form.contact, [key]: e.target.value } })} data-testid={`admin-contact-${key}-input`} />
          ))}
        </div>
        <p className="mt-1 text-xs text-slate-500">Handles (e.g. @quizmoney) or full links both work. Leave blank to hide.</p>
      </div>
      <button className="btn-primary w-full" data-testid="admin-season-save-button">Save season</button>
    </form>
    <Reminders />
    <AdminAccount />
    </div>
  );
}
